import React, { useEffect, useMemo } from 'react';
import { Alert, App, Button, Card, Col, DatePicker, Form, Input, Row, Select, Space, Statistic } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import employeeService, { employeeKeys } from '@/services/employee.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { getOrFindFromList } from './shared/hrmsFetch';

const LeaveApplicationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id) && id !== 'new';
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();

  const employeeId = Form.useWatch('employee_id', form);
  const leaveType = Form.useWatch('leave_type', form);
  const dates = Form.useWatch('dates', form);

  const { data: leaveTypes = [] } = useQuery({
    queryKey: hrmsKeys.leaveTypes(),
    queryFn: () => hrmsService.listLeaveTypes(),
  });

  const leaveTypeOptions = useMemo(() => {
    if (leaveTypes.length) {
      return leaveTypes.map((t: any) => ({
        value: t.leave_code || t.leave_name || 'Leave type',
        label: `${t.leave_name || t.leave_code}${t.is_paid === false ? ' (unpaid)' : ''}`,
      }));
    }
    return ['ANNUAL', 'SICK', 'UNPAID'].map((v) => ({ value: v, label: v }));
  }, [leaveTypes]);

  const { data: balances = [] } = useQuery({
    queryKey: employeeKeys.leaveBalances(employeeId || ''),
    queryFn: () => employeeService.listLeaveBalances(employeeId!),
    enabled: Boolean(employeeId),
  });

  const year = dates?.[0] ? dayjs(dates[0]).year() : dayjs().year();
  const balanceRow = useMemo(() => {
    if (!leaveType) return null;
    return (
      balances.find(
        (b: any) =>
          Number(b.leave_year) === year &&
          String(b.leave_type).toUpperCase() === String(leaveType).toUpperCase()
      ) || null
    );
  }, [balances, leaveType, year]);

  const available =
    balanceRow?.available_days != null
      ? Number(balanceRow.available_days)
      : balanceRow
        ? Number(balanceRow.opening_balance || 0) +
          Number(balanceRow.accrued || 0) +
          Number(balanceRow.carried_forward || 0) -
          Number(balanceRow.consumed || 0) -
          Number(balanceRow.encashed || 0) -
          Number(balanceRow.pending || 0)
        : null;

  const requestedDays =
    dates?.[0] && dates?.[1] ? dates[1].startOf('day').diff(dates[0].startOf('day'), 'day') + 1 : null;

  const { data: existing } = useQuery({
    queryKey: hrmsKeys.leaveApplication(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getLeaveApplication(id!),
        () => hrmsService.listLeaveApplications(),
        id!
      ),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    form.setFieldsValue({
      employee_id: existing.employee_id,
      leave_type: existing.leave_type,
      dates:
        existing.from_date && existing.to_date
          ? [dayjs(existing.from_date), dayjs(existing.to_date)]
          : undefined,
      reason: existing.reason,
    });
  }, [existing, form]);

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = {
        employee_id: v.employee_id,
        leave_type: v.leave_type,
        from_date: v.dates[0].format('YYYY-MM-DD'),
        to_date: v.dates[1].format('YYYY-MM-DD'),
        reason: v.reason,
      };
      if (isEdit) {
        await hrmsService.updateLeaveApplication(id!, body);
        message.success('Leave updated');
        navigate(`/hr/leaves/${id}`);
      } else {
        const created = await hrmsService.createLeaveApplication(body);
        message.success('Leave application created');
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveApplications() });
        navigate(`/hr/leaves/${created?.id || ''}`);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? 'Edit leave application' : 'New leave application'}
          documentNumber={existing?.leave_application_number}
          subtitle="Capture leave dates and reason — balance checked on submit"
          primaryActions={
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(isEdit ? `/hr/leaves/${id}` : '/hr/leaves')}
              >
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      <Form form={form} layout="vertical">
        <Card title="Application" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" disabled={isEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="leave_type" label="Leave type" rules={[{ required: true }]}>
                <Select options={leaveTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="dates" label="Leave period" rules={[{ required: true }]}>
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="reason" label="Reason">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>

      {employeeId && leaveType && (
        <Card title="Balance check" size="small">
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Statistic title={`Available (${year})`} value={available ?? '—'} suffix="days" />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Requested" value={requestedDays ?? '—'} suffix="days" />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Opening" value={Number(balanceRow?.opening_balance || 0)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Pending" value={Number(balanceRow?.pending || 0)} />
            </Col>
          </Row>
          {available != null && requestedDays != null && requestedDays > available && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="Requested days exceed available balance — submit will be blocked (except unpaid types)"
            />
          )}
          {!balanceRow && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="No opening balance for this type/year — set it under Leave openings or the employee profile Leave tab"
            />
          )}
        </Card>
      )}
    </WorkspaceLayout>
  );
};

export default LeaveApplicationFormPage;
